import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Box, Grid, Spacer, Text } from "@flexisaf/flexibull2";

import { HookForm as Form, Stepper } from "@/components";
import { useStepper } from "@/components/stepper/Stepper";
import { ContentBaseWrapper } from "./productIntegrationStyles";
import { uploadPaymentFormValidation } from "../productIntegrationValidation";
import { useProductIntegration } from "../productIntegrationUtils";
import { theme } from "@/style";
import FileUploader from "./FileUploader";

// Accepted file types: PDF and images
const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function UploadPaymentForm() {
    const { addToValues, values, isEditMode } = useProductIntegration();
    const { handleNext } = useStepper();
    const fileUploaderRef = useRef(null);
    const [uploadedFile, setUploadedFile] = useState(values.paymentFormFile || null);

    const form = useForm({
        resolver: yupResolver(uploadPaymentFormValidation),
        defaultValues: {
            firstName: values.firstName || "",
            lastName: values.lastName || "",
            jobTitle: values.jobTitle || "",
            email: values.email || "",
            phoneNumber: values.phoneNumber || "",
            paymentFormFile: values.paymentFormFile || null,
        },
    });

    useEffect(() => {
        if (!values.paymentFormFile) return;
        setUploadedFile(values.paymentFormFile);
        if (!form.formState.isDirty || isEditMode) {
            form.reset({
                firstName: values.firstName || "",
                lastName: values.lastName || "",
                jobTitle: values.jobTitle || "",
                email: values.email || "",
                phoneNumber: values.phoneNumber || "",
                paymentFormFile: values.paymentFormFile || null,
            });
        } else {
            form.setValue("paymentFormFile", values.paymentFormFile, { shouldValidate: true });
        }
    }, [form, isEditMode, values]);

    const handleFileUpload = (file) => {
        if (file) {
            setUploadedFile(file);
            form.setValue("paymentFormFile", file, { shouldValidate: true });
        } else {
            setUploadedFile(null);
            form.setValue("paymentFormFile", null, { shouldValidate: true });
        }
    };

    const onSubmit = (formBody) => {
        addToValues({
            firstName: formBody.firstName,
            lastName: formBody.lastName,
            jobTitle: formBody.jobTitle,
            email: formBody.email,
            phoneNumber: formBody.phoneNumber,
            paymentFormFile: uploadedFile,
        });
        handleNext();
    };

    const handleRemoveExisting = () => {
        setUploadedFile(null);
        form.setValue("paymentFormFile", null, { shouldValidate: true });
        addToValues({
            paymentFormFile: null,
            existingPaymentFormUrl: "",
            existingPaymentFormPath: "",
        });
    };

    const fileError = form.formState.errors?.paymentFormFile?.message;

    return (
        <ContentBaseWrapper>
            <div className="content">
                <Form form={form}>
                    <Box margin="2.5em 0">
                        <Text size="1rem">
                            Contact Information
                        </Text>
                        <Spacer space={4} />
                        <Text style={{ marginBottom: "1.5em", color: theme.PrimaryGrey }}>
                            Please provide the contact details for this integration.
                        </Text>
                    </Box>
                    <Grid default="1fr" sm="1fr" md="1fr" lr="1fr" gap="2em" style={{ maxWidth: "30em", marginBottom: "2em" }}>
                        <Grid default="1fr 1fr" sm="1fr" lr="1fr 1fr" gap="2em">
                            <Form.Input 
                                label="First Name" 
                                name="firstName" 
                                placeholder="Enter first name"
                                spaceBottom 
                            />
                            <Form.Input 
                                label="Last Name" 
                                name="lastName" 
                                placeholder="Enter last name"
                                spaceBottom 
                            />
                        </Grid>
                        <Form.Input 
                            label="Job Title" 
                            name="jobTitle" 
                            placeholder="Enter job title"
                            spaceBottom 
                            width="100%" 
                        />
                        <Form.Input 
                            label="Email" 
                            name="email" 
                            type="email" 
                            placeholder="Enter email address"
                            spaceBottom 
                            width="100%" 
                        />
                        <Form.Input 
                            label="Phone Number" 
                            name="phoneNumber" 
                            type="tel"
                            placeholder="Enter phone number"
                            spaceBottom 
                            width="100%" 
                        />
                    </Grid>
                    <Box margin="2.5em 0 2em">
                        <Text size="1rem">
                            Upload Payment Form
                        </Text>
                        <Spacer space={4} />
                        <Text style={{ color: theme.PrimaryGrey }}>
                            Please upload a PDF or image file (JPG, PNG) of the payment form.
                        </Text>
                        <Spacer space={4} />
                        <FileUploader
                            ref={fileUploaderRef}
                            acceptedFileTypes={acceptedFileTypes}
                            onFileUpload={handleFileUpload}
                            maxFileSize={MAX_FILE_SIZE}
                            existingFileUrl={uploadedFile?.isExistingFile ? uploadedFile?.url : ""}
                            existingFileName={uploadedFile?.isExistingFile ? uploadedFile?.name : ""}
                            existingFileType={uploadedFile?.isExistingFile ? uploadedFile?.type : ""}
                            onRemoveExisting={handleRemoveExisting}
                        />
                        {fileError && (
                            <Text color={theme.PrimaryRed} style={{ marginTop: "0.5em" }}>
                                {fileError}
                            </Text>
                        )}
                    </Box>
                </Form>
            </div>
            <Stepper.StepButtons onNext={form.handleSubmit(onSubmit)} />
        </ContentBaseWrapper>
    );
}
